import { Link } from "react-router-dom";
import "@jkast90/kast-common/table.css";
import { SimpleTable } from "@jkast90/kast-common/table";
import { LegalLinks } from "@jkast90/kast-common/legal/react";
import { Page } from "@jkast90/kast-common/ui";
import { CORE_VERSION, EJS_VERSION } from "../emulator/core-version";

const out = { target: "_blank", rel: "noreferrer" } as const;

export default function AboutPage() {
  return (
    <Page className="about-main">
      <div className="library-head">
        <h1>About</h1>
      </div>

      <section className="panel">
        <h2>CloudBoy</h2>
        <p>
          A personal console. Game Boy, Game Boy Color, Game Boy Advance and Nintendo 64 games play in the browser; the
          desktop app adds PlayStation, PSP and the original Xbox, at native speed on a Mac. Games you add stay on your
          device. Playing needs no account; sign in with a kast-dev account and the rest follows you between devices.
        </p>
      </section>

      <section className="panel">
        <h2>What it does</h2>
        <ul className="about-list">
          <li>
            <strong>Library</strong>: every game you have, on this device or in the shared cloud library, with its
            cover, your play time beside everyone's, and who's playing it. Add a game, or scan a folder of them.
          </li>
          <li>
            <strong>Saves</strong>: in-game saves and save states sync to the cloud as you play, with a quick save in
            the player, and each account keeps its own on a shared device.
          </li>
          <li>
            <strong>Keyboard</strong>: one layout for every console and both apps; click any key in the Keyboard panel
            to change it.
          </li>
          <li>
            <strong>Walkthroughs</strong>: Pokémon games get a guide beside the screen that knows which game is running
            and remembers where you were.
          </li>
          <li>
            <strong>Phones</strong>: an on-screen pad, landscape play, and a home-screen app on iPhone and Android.
          </li>
        </ul>
      </section>

      <section className="panel">
        <h2>Emulators</h2>
        <SimpleTable
          className="about-table"
          headers={["Systems", "Emulator", "Version", "License"]}
          rows={[
            [
              "Game Boy, Game Boy Color, Game Boy Advance",
              <>
                <a href="https://mgba.io" {...out}>
                  mGBA
                </a>
                , compiled to WebAssembly by{" "}
                <a href="https://github.com/thenick775/mgba/tree/feature/wasm" {...out}>
                  thenick775/mgba
                </a>
              </>,
              CORE_VERSION,
              "MPL-2.0",
            ],
            [
              "Nintendo 64",
              <>
                <a href="https://emulatorjs.org" {...out}>
                  EmulatorJS
                </a>{" "}
                running the{" "}
                <a href="https://github.com/libretro/mupen64plus-libretro-nx" {...out}>
                  mupen64plus-next
                </a>{" "}
                libretro core
              </>,
              EJS_VERSION,
              "GPL-3.0 (EmulatorJS), GPL-2.0 (mupen64plus-next)",
            ],
            [
              "PlayStation (desktop app)",
              <>
                <a href="https://github.com/libretro/swanstation" {...out}>
                  SwanStation
                </a>{" "}
                libretro core
              </>,
              "nightly",
              "GPL-3.0",
            ],
            [
              "PSP (desktop app)",
              <>
                <a href="https://www.ppsspp.org" {...out}>
                  PPSSPP
                </a>{" "}
                libretro core
              </>,
              "nightly",
              "GPL-2.0",
            ],
            [
              "Xbox (desktop app)",
              <>
                <a href="https://xemu.app" {...out}>
                  xemu
                </a>
                , installed separately
              </>,
              "your copy",
              "GPL-2.0",
            ],
          ]}
        />
        <p className="kui-muted kui-small">
          The browser cores run multithreaded, which is why the site is served cross-origin isolated and loads nothing
          from third-party origins. mGBA ships a high-level BIOS replacement, so no BIOS file is needed; xemu needs the
          console's own files, which the app doesn't ship.
        </p>
      </section>

      <section className="panel">
        <h2>How it is built</h2>
        <ul className="about-list">
          <li>
            <strong>Web app</strong>: React, TypeScript and Vite, served from S3 through CloudFront.
          </li>
          <li>
            <strong>Desktop app</strong>: the same web app in a Tauri window, with a Rust game process that hosts a
            libretro core per game.
          </li>
          <li>
            <strong>Cloud saves and library</strong>: a Rust API (axum on AWS Lambda) behind the shared kast-dev API
            Gateway, storing opaque blobs in a private S3 bucket keyed by your account. Uploads and downloads go
            straight to S3 through short-lived presigned URLs.
          </li>
          <li>
            <strong>Sign-in</strong>: the shared kast-dev Cognito user pool via login.kast-dev.net. Playing never
            requires an account.
          </li>
          <li>
            <strong>Android</strong>: a Trusted Web Activity that runs this site in Chrome, built with{" "}
            <a href="https://github.com/GoogleChrome/android-browser-helper" {...out}>
              android-browser-helper
            </a>
            .
          </li>
          <li>
            <strong>Walkthroughs</strong>: written for this app and generated into Markdown, rendered in the guide
            panel. The game is identified from the ROM header, not the file name.
          </li>
          <li>
            <strong>Icons</strong>: Material Symbols (Apache-2.0), inlined as SVG paths.
          </li>
          <li>
            <strong>Console drawings</strong>: the Systematic theme by Baxy from libretro's{" "}
            <a href="https://github.com/libretro/retroarch-assets" {...out}>
              retroarch-assets
            </a>{" "}
            (
            <a href="https://creativecommons.org/licenses/by/4.0/" {...out}>
              CC BY 4.0
            </a>
            ), used unchanged. Box art comes from libretro-thumbnails.
          </li>
        </ul>
      </section>

      <section className="panel">
        <h2>Something broken?</h2>
        <p>
          <Link to="/bug-report">Report a bug</Link>. The report carries the app's recent cloud calls, its log and the
          pages you visited, with sign-in tokens taken out.
        </p>
      </section>

      <section className="panel">
        <h2>Legal</h2>
        <p className="kui-muted kui-small">
          CloudBoy ships no games, no BIOS and no copyrighted assets. Use dumps of cartridges and discs you own. Game
          Boy, Game Boy Advance, Nintendo 64 and Pokémon are trademarks of Nintendo; PlayStation and PSP of Sony; Xbox
          of Microsoft. This project is not affiliated with or endorsed by any of them.
        </p>
        <p className="kui-muted kui-small">
          Each visit records your IP address, how many times it has visited and when it was first and last seen, to
          count visitors and spot abuse. It is kept for 90 days after the last visit. Only CloudBoy's admins can see it.
        </p>
        <LegalLinks className="about-legal" />
      </section>
    </Page>
  );
}
